import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import { createSimpleMiddleware } from './createSimpleMiddleware';
import { AppState } from './store';
import { fetchSessionForAuthFailure } from './sessionModel';

export const IMPERSONATION_HEADER_KEY = 'waterlily-impersonate-advisor-id';

export function getStoredImpersonationTarget(): string | null {
  return localStorage.getItem(IMPERSONATION_HEADER_KEY);
}

function setStoredImpersonationTarget(targetAdvisorId: string): void {
  localStorage.setItem(IMPERSONATION_HEADER_KEY, targetAdvisorId);
}

export interface ImpersonationState {
  targetAdvisorId: string | null;
}

const impersonationSlice = createSlice({
  name: 'impersonation',
  initialState: { targetAdvisorId: null } as ImpersonationState,
  reducers: {
    startImpersonation(state, { payload }: PayloadAction<string>) {
      state.targetAdvisorId = payload;
    },
    stopImpersonation(state) {
      state.targetAdvisorId = null;
    },
    initImpersonation(state, { payload }: PayloadAction<string | null>) {
      state.targetAdvisorId = payload;
    },
  },
});

function impersonationMiddlewareImpl(
  dispatch: Dispatch<Action>,
  getStore: () => AppState,
  next: Dispatch<Action>,
  action: Action,
) {
  next(action);

  if (action.type === startImpersonation.type) {
    const targetAdvisorId = (action as PayloadAction<string>).payload;
    setStoredImpersonationTarget(targetAdvisorId);
    window.location.reload();
  }

  if (action.type === stopImpersonation.type) {
    const wasImpersonating = localStorage.getItem(IMPERSONATION_HEADER_KEY) !== null;
    localStorage.removeItem(IMPERSONATION_HEADER_KEY);
    if (wasImpersonating) {
      window.location.reload();
    }
  }

  // Clear stale impersonation targets when session fetch fails
  if (action.type === fetchSessionForAuthFailure.type) {
    const state = getStore();
    const isImpersonating = state.impersonation.targetAdvisorId !== null;

    // If we were impersonating and the session fetch failed, clear the impersonation
    // to prevent infinite login/logout loops from stale localStorage values
    if (isImpersonating) {
      localStorage.removeItem(IMPERSONATION_HEADER_KEY);
      dispatch(stopImpersonation());
    }
  }
}

export const impersonationMiddleware = createSimpleMiddleware(impersonationMiddlewareImpl);

export const { startImpersonation, stopImpersonation, initImpersonation } =
  impersonationSlice.actions;

export const impersonationReducer = impersonationSlice.reducer;
